import React, { useEffect, useState, useContext, JSX } from 'react'
import axios from 'axios'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import AuthContext from '../../middlewares/AuthContext'
import jwt_decode from 'jwt-decode'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxOpen, faSave } from '@fortawesome/free-solid-svg-icons'
import CustomLoader from '../layouts/CustomLoader'
import { isMultiplatformUI } from '../../utilities/roles'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
  token     : string | null
}

interface DecodedToken {
  roles: { name: string }[]
}

interface Product {
  id             : number
  name           : string
  default_checked?: boolean
  selectable?    : boolean
}

interface PackageForm {
  quantity         : number
  comments         : string
  product          : number
  TimeId           : number
  expiry_time      : number
  compatibility    : boolean
  compatibility_smp: boolean
}

interface MultiplatformPackageForm {
  quantity   : number
  comments   : string
  products   : number[]
  TimeId     : number
  expiry_time: number
}

const INITIAL: PackageForm = {
  quantity         : 0,
  comments         : '',
  product          : 0,
  TimeId           : 0,
  expiry_time      : 0,
  compatibility    : false,
  compatibility_smp: false,
}

const formSchema = Yup.object().shape({
  quantity         : Yup.number().min(10).max(1000).required(),
  comments         : Yup.string().min(1).max(255).required(),
  product          : Yup.number().min(1).required(),
  TimeId           : Yup.number().min(1).max(3).required(),
  expiry_time      : Yup.number().min(1).max(31).required(),
  compatibility    : Yup.boolean().required(),
  compatibility_smp: Yup.boolean().required(),
})

const multiplatformFormSchema = Yup.object().shape({
  quantity   : Yup.number().min(10).max(1000).required(),
  comments   : Yup.string().min(1).max(255).required(),
  products   : Yup.array().of(Yup.number()).min(1).required(),
  TimeId     : Yup.number().min(1).max(3).required(),
  expiry_time: Yup.number().min(1).max(31).required(),
})

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const getExpiryOptions = (timeId: number, isAdmin: boolean): JSX.Element[] => {
  let max = 0
  if (timeId === 1) max = isAdmin ? 3 : 2
  if (timeId === 2) max = 25
  if (timeId === 3) max = 32
  const opts: JSX.Element[] = []
  for (let i = 1; i < max; i++) {
    opts.push(<option key={i} value={i}>{i}</option>)
  }
  return opts
}

const CreatePackages: React.FC = () => {
  const { getHeaders, token } = useContext(AuthContext) as AuthCtx
  const { t }                 = useTranslation()

  const decoded         = token ? jwt_decode<DecodedToken>(token) : null
  const role            = decoded?.roles?.[0]?.name ?? ''
  const isAdmin         = role === 'admin'
  const isMultiplatform = isMultiplatformUI(role)

  const [loaded, setLoaded]   = useState(false)
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get<{ message: Product[] }>(`${url}packages/get-products`, { headers })
        setProducts(res.data.message)
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetchProducts()
  }, [])

  const handleSubmit = async (values: PackageForm | MultiplatformPackageForm, { resetForm }: { resetForm: () => void }) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ response: string }>(`${url}packages/store`, values, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
      resetForm()
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  return (
    <div className="row justify-content-center">
      <div className="col-md-10 col-lg-8">

        {/* Header card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
          marginBottom   : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faBoxOpen} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('packages.create.create_pack_licenses')}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {t(`packages.create.${role}`)}
              </p>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>

            <Formik
              initialValues={isMultiplatform
                ? {
                    quantity   : 0,
                    comments   : '',
                    products   : products.filter(p => p.default_checked).map(p => p.id),
                    TimeId     : 1,
                    expiry_time: 1,
                  } as MultiplatformPackageForm
                : INITIAL}
              validationSchema={isMultiplatform ? multiplatformFormSchema : formSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form>

                  {/* Quantity */}
                  <div className="mb-4">
                    <FieldLabel>{t('packages.create.quantity')} *</FieldLabel>
                    <Field type="number" name="quantity" className="form-control form-control-sm" />
                    <ErrorMessage name="quantity" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  {isMultiplatform ? (
                    <div className="mb-4">
                      <FieldLabel>{t('packages.create.select_apps')} *</FieldLabel>
                      <div style={{
                        padding: '0.5rem 0.75rem', borderRadius: 8, backgroundColor: `${BRAND}0d`,
                        border: `1px solid ${BRAND}55`, fontSize: '0.78rem', color: BRAND_DARK,
                        marginBottom: '0.6rem', fontWeight: 600,
                      }}>
                        {t('packages.create.multiplatform_legend')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {products.map((p, i) => {
                          const selectedIds = (values as MultiplatformPackageForm).products || []
                          const checked     = selectedIds.includes(p.id)
                          const disabled    = p.selectable === false
                          const toggle = () => {
                            if (disabled) return
                            setFieldValue('products', checked
                              ? selectedIds.filter(id => id !== p.id)
                              : [...selectedIds, p.id])
                          }
                          return (
                            <label key={i} style={{
                              display       : 'flex',
                              alignItems    : 'center',
                              gap           : '0.6rem',
                              padding       : '0.5rem 0.75rem',
                              borderRadius  : 8,
                              border        : `1px solid ${checked ? BRAND : '#e2e8f0'}`,
                              backgroundColor: disabled ? '#f1f5f9' : (checked ? `${BRAND}0d` : '#f8fafc'),
                              cursor        : disabled ? 'not-allowed' : 'pointer',
                              opacity       : disabled ? 0.55 : 1,
                              transition    : 'all 0.15s',
                            }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled}
                                onChange={toggle}
                                style={{ accentColor: BRAND }}
                              />
                              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', textTransform: 'capitalize' }}>
                                {p.name}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                      <ErrorMessage name="products" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  ) : (
                    <div className="mb-4">
                      <FieldLabel>{t('packages.compatibility')} *</FieldLabel>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {products.map((p, i) => (
                          <label key={i} style={{
                            display       : 'flex',
                            alignItems    : 'center',
                            gap           : '0.6rem',
                            padding       : '0.5rem 0.75rem',
                            borderRadius  : 8,
                            border        : `1px solid ${(values as PackageForm).product === p.id ? BRAND : '#e2e8f0'}`,
                            backgroundColor: (values as PackageForm).product === p.id ? `${BRAND}0d` : '#f8fafc',
                            cursor        : 'pointer',
                            transition    : 'all 0.15s',
                          }}>
                            <input
                              type="radio"
                              name="product"
                              value={p.id}
                              checked={(values as PackageForm).product === p.id}
                              onChange={() => setFieldValue('product', p.id)}
                              style={{ accentColor: BRAND }}
                            />
                            <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', textTransform: 'capitalize' }}>
                              {p.name}
                            </span>
                          </label>
                        ))}
                      </div>
                      <ErrorMessage name="product" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  )}

                  {/* Compatibility checkboxes (only for product 1, legacy single-app flow) */}
                  {!isMultiplatform && (values as PackageForm).product === 1 && (
                    <div className="mb-4">
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: '0.6rem',
                        marginBottom: '0.5rem', cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox"
                          style={{ accentColor: BRAND, width: 16, height: 16 }}
                          onChange={e => setFieldValue('compatibility', e.target.checked)}
                          checked={(values as PackageForm).compatibility}
                        />
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', textTransform: 'capitalize' }}>
                          {t('packages.create.compatible')}
                        </span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          style={{ accentColor: BRAND, width: 16, height: 16 }}
                          onChange={e => setFieldValue('compatibility_smp', e.target.checked)}
                          checked={(values as PackageForm).compatibility_smp}
                        />
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
                          Compatible con Suite Mobile POS
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Time unit */}
                  <div className="mb-4">
                    <FieldLabel>{t('packages.create.select_an_time')} *</FieldLabel>
                    <Field
                      as="select"
                      name="TimeId"
                      className="form-select form-select-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setFieldValue('TimeId', parseInt(e.target.value))
                        setFieldValue('expiry_time', 0)
                      }}
                    >
                      <option value={0} disabled>{t('packages.create.select_an_time')}</option>
                      {isAdmin
                        ? ['Years', 'Months', 'Days'].map((label, i) => (
                            <option key={i} value={i + 1}>{label}</option>
                          ))
                        : <option value={1}>{t('utilities.years')}</option>
                      }
                    </Field>
                    <ErrorMessage name="TimeId" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  {/* Expiry time */}
                  <div className="mb-4">
                    <FieldLabel>{t('packages.create.select_expire_time')} *</FieldLabel>
                    <Field as="select" name="expiry_time" className="form-select form-select-sm">
                      <option value={0} disabled>{t('packages.create.select_expire_time')}</option>
                      {getExpiryOptions(parseInt(String(values.TimeId)), isAdmin)}
                    </Field>
                    <ErrorMessage name="expiry_time" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  {/* Comments */}
                  <div className="mb-4">
                    <FieldLabel>{t('packages.create.comments')} *</FieldLabel>
                    <Field as="textarea" name="comments" className="form-control form-control-sm" style={{ height: 90 }} />
                    <ErrorMessage name="comments" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      display        : 'inline-flex',
                      alignItems     : 'center',
                      justifyContent : 'center',
                      gap            : '0.5rem',
                      width          : '100%',
                      padding        : '0.55rem 1.5rem',
                      borderRadius   : 9999,
                      backgroundColor: loading ? '#94a3b8' : BRAND,
                      color          : '#fff',
                      fontWeight     : 700,
                      fontSize       : '0.875rem',
                      border         : 'none',
                      cursor         : loading ? 'not-allowed' : 'pointer',
                      boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                      transition     : 'filter 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                  >
                    {loading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : <FontAwesomeIcon icon={faSave} />
                    }
                    <span>{t('packages.create.create_package')}</span>
                  </button>

                </Form>
              )}
            </Formik>
          </div>
        </div>

      </div>
    </div>
  )
}

export default CreatePackages
